import { Grid } from "models/Grid";
import { Spot } from "models/Spot";
import { heuristic, movementCost } from "utils/math";

function createPathfinder({
  grid,
  columns = 28,
  rows = 28,
  wallProbability = 0.24,
  random = Math.random,
} = {}) {
  let simulationGrid =
    grid ??
    new Grid({
      columns,
      rows,
      wallProbability,
      random,
      spot: Spot,
    });
  let openSet = [];
  let status = "idle";
  let stepCount = 0;
  let path = [];

  function buildPath(target) {
    const nextPath = [];
    let current = target;

    while (current) {
      nextPath.unshift(current);
      current = current.previous;
    }

    return nextPath;
  }

  function updatePath(nextPath) {
    simulationGrid.items.forEach((item) => {
      item.isPath = false;
    });

    nextPath.forEach((item) => {
      if (!item.start && !item.end) {
        item.isPath = true;
      }
    });

    path = nextPath;
  }

  function chooseNextSpot() {
    return openSet.reduce((bestSpot, candidate) => {
      if (candidate.totalCost < bestSpot.totalCost) {
        return candidate;
      }

      if (
        candidate.totalCost === bestSpot.totalCost &&
        candidate.costToFinish < bestSpot.costToFinish
      ) {
        return candidate;
      }

      if (
        candidate.totalCost === bestSpot.totalCost &&
        candidate.costToFinish === bestSpot.costToFinish &&
        candidate.currentPathCost < bestSpot.currentPathCost
      ) {
        return candidate;
      }

      return bestSpot;
    }, openSet[0]);
  }

  function canTraverse(current, neighbor) {
    const isDiagonal = current.x !== neighbor.x && current.y !== neighbor.y;

    if (!isDiagonal) {
      return true;
    }

    const horizontalNeighbor = simulationGrid.getSpot(neighbor.x, current.y);
    const verticalNeighbor = simulationGrid.getSpot(current.x, neighbor.y);

    return !(horizontalNeighbor?.wall && verticalNeighbor?.wall);
  }

  function initializeSearch() {
    simulationGrid.resetSearch();
    openSet = [simulationGrid.start];
    status = "searching";
    stepCount = 0;
    path = [simulationGrid.start];

    simulationGrid.start.isOpen = true;
    simulationGrid.start.currentPathCost = 0;
    simulationGrid.start.costToFinish = heuristic(
      simulationGrid.start,
      simulationGrid.end,
    );
    simulationGrid.start.totalCost = simulationGrid.start.costToFinish;
  }

  function resetSearch() {
    initializeSearch();

    return getSnapshot();
  }

  function regenerate(options = {}) {
    simulationGrid = new Grid({
      columns: options.columns ?? columns,
      rows: options.rows ?? rows,
      wallProbability: options.wallProbability ?? wallProbability,
      random: options.random ?? random,
      spot: Spot,
    });

    initializeSearch();

    return getSnapshot();
  }

  function step() {
    if (status !== "searching") {
      return getSnapshot();
    }

    if (openSet.length === 0) {
      status = "blocked";
      updatePath([]);

      return getSnapshot();
    }

    const current = chooseNextSpot();
    openSet = openSet.filter((item) => item.id !== current.id);
    current.isOpen = false;
    current.isClosed = true;
    stepCount += 1;

    if (current.id === simulationGrid.end.id) {
      status = "found";
      updatePath(buildPath(current));

      return getSnapshot();
    }

    current.neighbors.forEach((neighbor) => {
      if (
        neighbor.wall ||
        neighbor.isClosed ||
        !canTraverse(current, neighbor)
      ) {
        return;
      }

      const candidateCost =
        current.currentPathCost + movementCost(current, neighbor);

      if (!neighbor.isOpen || candidateCost < neighbor.currentPathCost) {
        neighbor.previous = current;
        neighbor.currentPathCost = candidateCost;
        neighbor.costToFinish = heuristic(neighbor, simulationGrid.end);
        neighbor.totalCost = neighbor.currentPathCost + neighbor.costToFinish;

        if (!neighbor.isOpen) {
          neighbor.isOpen = true;
          openSet.push(neighbor);
        }
      }
    });

    updatePath(buildPath(current));

    return getSnapshot();
  }

  function getSnapshot() {
    const exploredCount = simulationGrid.items.filter(
      (item) => item.isClosed,
    ).length;
    const frontierCount = simulationGrid.items.filter(
      (item) => item.isOpen,
    ).length;

    return {
      grid: simulationGrid,
      status,
      stepCount,
      exploredCount,
      frontierCount,
      pathLength: Math.max(path.length - 1, 0),
      path,
      start: simulationGrid.start,
      end: simulationGrid.end,
    };
  }

  initializeSearch();

  return {
    getSnapshot,
    regenerate,
    resetSearch,
    step,
  };
}

export { createPathfinder };
